"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import ProgramCard from "@/components/page-section/ProgramCard/ProgramCard";
import { useSectionHeight } from "@/hooks";

interface Program {
  id: string | number;
  title: string;
  description: string;
  image: string;
  badge: string;
  href?: string;
}

interface ProgramListProps {
  programs: Program[];
}

const ITEMS_PER_PAGE = 6;

const TienSiProgramList: React.FC<ProgramListProps> = ({ programs }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isDesktop, setIsDesktop] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const { scrollOffset } = useSectionHeight({ fullHeight: false });

  const totalPages = Math.ceil(programs.length / ITEMS_PER_PAGE);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const currentPrograms = useMemo(() => {
    if (!isDesktop) {
      return programs;
    }
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return programs.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [programs, currentPage, isDesktop]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      const element = document.getElementById("program-cards");
      if (element) {
        const top = element.getBoundingClientRect().top + window.scrollY - scrollOffset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
  };

  const handleMobileSlide = (direction: "prev" | "next") => {
    if (!gridRef.current) return;
    const firstCard = gridRef.current.querySelector(".program-card-item") as HTMLElement | null;
    const step = (firstCard?.offsetWidth ?? gridRef.current.clientWidth) + 16;
    gridRef.current.scrollBy({
      left: direction === "next" ? step : -step,
      behavior: "smooth",
    });
  };

  return (
    <>
      <div ref={gridRef} className="program-cards-grid">
        {currentPrograms.map((program) => (
          <div key={String(program.id)} className="program-card-item">
            <ProgramCard
              program={{
                ...program,
                id: program.id,
                href: program.href ?? "/cac-nganh-dao-tao/tien-si-dao-tao",
              }}
            />
          </div>
        ))}
      </div>

      {/* Pagination - Desktop/Tablet */}
      {isDesktop && totalPages > 1 && (
        <nav
          className="program-pagination d-none d-md-flex"
          aria-label="Phân trang ngành đào tạo"
        >
          <button
            className="program-pagination__btn program-pagination__btn--prev"
            aria-label="Trang trước"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <Image src="/assets/svg/d-arrow-left.svg" alt="" width={11} height={10} />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`program-pagination__item ${currentPage === page ? "program-pagination__item--active" : ""}`}
              type="button"
              aria-label={`Trang ${page}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="program-pagination__btn program-pagination__btn--next"
            aria-label="Trang kế tiếp"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <Image src="/assets/svg/d-arrow-right.svg" alt="" width={11} height={10} />
          </button>
        </nav>
      )}

      {/* Mobile Slider Navigation */}
      <nav className="program-slider-nav d-sm-none" aria-label="Điều hướng slider">
        <button
          className="program-slider-nav__btn program-slider-nav__btn--prev"
          type="button"
          aria-label="Slide trước"
          onClick={() => handleMobileSlide("prev")}
        >
          <Image
            src="/assets/svg/d-arrow-left.svg"
            alt=""
            width={11}
            height={10}
            loading="lazy"
          />
        </button>
        <button
          className="program-slider-nav__btn program-slider-nav__btn--next"
          type="button"
          aria-label="Slide sau"
          onClick={() => handleMobileSlide("next")}
        >
          <Image
            src="/assets/svg/d-arrow-right.svg"
            alt=""
            width={11}
            height={10}
            loading="lazy"
          />
        </button>
      </nav>
    </>
  );
};

export default TienSiProgramList;
